import React, { useEffect, useState, useMemo } from "react";
import { axiosSalary, axiosMaster } from "../../api/axiosInstance";
import { FaFileExcel, FaSearch } from "react-icons/fa";

/* ─────────────────────────────────────────────
   Helper: is an employee active on a given date?

   Business Rules:
   1. relievingDate = null  → currently working → show
   2. relievingDate has a date → left company → hide after that date
   3. Rejoin support: checks each tenure in employmentHistory[]
      Falls back to single joiningDate/relievingDate for backward compat.
   ───────────────────────────────────────────── */
const isActiveOnDate = (emp, reportDate) => {
  const rd = new Date(reportDate);
  rd.setHours(0, 0, 0, 0);

  const tenures =
    Array.isArray(emp.employmentHistory) && emp.employmentHistory.length > 0
      ? emp.employmentHistory
      : [{ joiningDate: emp.joiningDate, relievingDate: emp.relievingDate ?? null }];

  return tenures.some((tenure) => {
    if (!tenure.joiningDate) return false;

    const join = new Date(tenure.joiningDate);
    join.setHours(0, 0, 0, 0);

    const relieve = tenure.relievingDate ? new Date(tenure.relievingDate) : null;
    if (relieve) relieve.setHours(0, 0, 0, 0);

    // Guard: invalid data (relieving before joining) → treat as active
    if (relieve && relieve < join) return join <= rd;

    return join <= rd && (!relieve || relieve >= rd);
  });
};

/* ─────────────────────────────────────────────
   CSV escape helper
   ───────────────────────────────────────────── */
const escapeCSV = (val) => {
  const str = String(val ?? "");
  const safe = /^[=+\-@]/.test(str) ? `'${str}` : str;
  return `"${safe.replace(/"/g, '""')}"`;
};

/* ═══════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════ */
const DailyAttendanceReport = () => {

  /* ─── USER ─── */
  let storedUser = {};
  try { storedUser = JSON.parse(localStorage.getItem("user") || "{}"); } catch { }
  const groupId = storedUser?.groupId;

  /* ─── STATE ─── */
  const [date, setDate] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL");

  /* ─── FETCH ─── */
  const fetchReport = async (reportDate) => {
    if (!reportDate) return;
    try {
      setLoading(true);

      const [attendanceRes, empRes] = await Promise.all([
        axiosSalary.get(
          `/DailyAttendanceReport/DailyReportByGroupAndDate?groupId=${groupId}&attendanceDate=${reportDate}`
        ),
        axiosMaster.get(`/EmployeeMaster/GetByGroup?groupId=${groupId}`),
      ]);

      const attendance = attendanceRes.data || [];

      // ✅ Filter employees active on the selected report date
      const employees = (empRes.data || []).filter((emp) =>
        isActiveOnDate(emp, reportDate)
      );

      setData(mergeAttendanceWithEmployees(attendance, employees, reportDate));

    } catch (err) {
      console.error("Report error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ─── MERGE ───
     Suspense employees are treated as Absent everywhere:
     minutes zeroed out, punches cleared, isAbsent forced true. */
  const mergeAttendanceWithEmployees = (attendance, employees, reportDate) => {
    const map = new Map();
    attendance.forEach((a) => map.set(a.empId, a));

    return employees.map((emp) => {
      if (map.has(emp.empId)) {
        const att = map.get(emp.empId);
        const isSuspense = !!att.isSuspense;

        if (isSuspense) {
          // Force this record to look and behave exactly like an Absent row
          return {
            ...att,
            empName: att.empName || emp.empName,
            firstPunchDisplay: null,
            lastPunchDisplay: null,
            totalPunches: 0,
            workingMinutes: 0,
            lateMinutes: 0,
            earlyLeaveMinutes: 0,
            overtimeMinutes: 0,
            isPresent: false,
            isAbsent: true,
            isLate: false,
            isSuspense: true,
            remarks: att.remarks || "Absent (Suspense)",
          };
        }

        return { ...att, empName: att.empName || emp.empName };
      }

      // Absent entry for active employee with no punch
      return {
        empId: emp.empId,
        empName: emp.empName,
        attendanceDateDisplay: reportDate,
        firstPunchDisplay: null,
        lastPunchDisplay: null,
        totalPunches: 0,
        workingMinutes: 0,
        lateMinutes: 0,
        earlyLeaveMinutes: 0,
        overtimeMinutes: 0,
        isPresent: false,
        isAbsent: true,
        isLate: false,
        isSuspense: false,
        remarks: "Absent",
      };
    });
  };

  /* ─── DEFAULT DATE ─── */
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setDate(today);
  }, []);

  useEffect(() => {
    if (date) fetchReport(date);
  }, [date]);

  /* ─── FILTER ─── */
  const filteredData = useMemo(() => {
    const term = search.toLowerCase();

    let filtered = data.filter(
      (d) =>
        d.empName?.toLowerCase().includes(term) ||
        String(d.empId).includes(term)
    );

    if (activeFilter === "PRESENT") filtered = filtered.filter((d) => d.isPresent && !d.isAbsent);
    else if (activeFilter === "ABSENT") filtered = filtered.filter((d) => d.isAbsent);
    else if (activeFilter === "SUSPENSE") {
      filtered = filtered.filter((d) => d.isSuspense);
    }
    else if (activeFilter === "LATE") filtered = filtered.filter((d) => d.isLate && !d.isAbsent);

    return filtered;
  }, [data, search, activeFilter]);

  useEffect(() => { setActiveFilter("ALL"); }, [search]);

  /* ─── SUMMARY ───
     isSuspense rows are now isAbsent === true (forced in merge step),
     so a single "absent" count naturally includes them. No separate
     suspense bucket is needed anymore. */
  const total = data.length;
  const present = data.filter((d) => d.isPresent && !d.isAbsent && !d.isSuspense).length;
  const absent = data.filter((d) => d.isAbsent && !d.isSuspense).length;
  const late = data.filter((d) => d.isLate && !d.isAbsent).length;
  const suspense = data.filter((d) => d.isSuspense).length;

  /* ─── EXPORT ─── */
  const exportExcel = () => {
    if (!filteredData.length) return;

    const rows = filteredData.map((r) => ({
      EmpId: r.empId,
      Name: r.empName || "",
      Date: r.attendanceDateDisplay,
      FirstPunch: r.firstPunchDisplay ? `\t${r.firstPunchDisplay}` : "",
      LastPunch: r.lastPunchDisplay ? `\t${r.lastPunchDisplay}` : "",
      Punches: r.totalPunches,
      WorkingMinutes: r.workingMinutes,
      LateMinutes: r.lateMinutes,
      EarlyLeaveMinutes: r.earlyLeaveMinutes,
      OvertimeMinutes: r.overtimeMinutes,
      Status: r.isAbsent ? "Absent" : r.isLate ? "Late" : r.isPresent ? "Present" : "",
      Remarks: r.remarks || "",
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((row) => Object.values(row).map(escapeCSV).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = `DailyAttendance_${date}.csv`;
    link.click();
  };


  /* ═══════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════ */
  return (
    <div className="bg-white p-4 md:p-5 rounded-xl shadow border">

      {/* ── HEADER ── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">

        <div className="flex items-center justify-between w-full md:w-auto">
          <h2 className="text-lg md:text-xl font-semibold">Daily Attendance Report</h2>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">

          <div className="relative w-full sm:w-56">
            <FaSearch className="absolute left-2 top-2.5 text-gray-400 text-xs" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-7 pr-2 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Search employee..."
            />
          </div>

          <div className="flex gap-2 w-full sm:w-auto">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border px-2 py-1.5 rounded-md text-sm w-full sm:w-auto focus:ring-2 focus:ring-blue-400"
            />
            <button
              onClick={exportExcel}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-sm flex items-center justify-center gap-2 w-full sm:w-auto transition"
            >
              <FaFileExcel /> Export
            </button>
          </div>

        </div>
      </div>

      {/* ── SUMMARY CARDS ── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 text-sm">

        <div
          onClick={() => setActiveFilter("ALL")}
          className={`bg-gray-100 p-3 rounded text-center cursor-pointer ${activeFilter === "ALL" ? "ring-2 ring-gray-400" : ""}`}
        >
          <p>Total</p>
          <p className="font-semibold">{total}</p>
        </div>

        <div
          onClick={() => setActiveFilter("PRESENT")}
          className={`bg-green-100 p-3 rounded text-center cursor-pointer ${activeFilter === "PRESENT" ? "ring-2 ring-green-500" : ""}`}
        >
          <p className="text-green-700">Present</p>
          <p className="font-semibold">{present}</p>
        </div>

        <div
          onClick={() => setActiveFilter("ABSENT")}
          className={`bg-red-100 p-3 rounded text-center cursor-pointer ${activeFilter === "ABSENT" ? "ring-2 ring-red-500" : ""}`}
        >
          <p className="text-red-700">Absent</p>
          <p className="font-semibold">{absent}</p>
        </div>
        <div
          onClick={() => setActiveFilter("SUSPENSE")}
          className={`bg-purple-100 p-3 rounded text-center cursor-pointer ${activeFilter === "SUSPENSE" ? "ring-2 ring-purple-500" : ""
            }`}
        >
          <p className="text-purple-700">Suspense</p>
          <p className="font-semibold">{suspense}</p>
        </div>

        <div
          onClick={() => setActiveFilter("LATE")}
          className={`bg-yellow-100 p-3 rounded text-center cursor-pointer ${activeFilter === "LATE" ? "ring-2 ring-yellow-500" : ""}`}
        >
          <p className="text-yellow-700">Late</p>
          <p className="font-semibold">{late}</p>
        </div>

      </div>

      {/* ── DESKTOP TABLE ── */}
      <div className="hidden md:block border rounded-lg max-h-[500px] overflow-auto">
        <table className="w-full text-xs border-collapse">

          <thead className="bg-gray-200 sticky top-0">
            <tr>
              <th className="border p-2">ID</th>
              <th className="border p-2 text-left">Name</th>
              <th className="border p-2">Date</th>
              <th className="border p-2">First</th>
              <th className="border p-2">Last</th>
              <th className="border p-2">Punch</th>
              <th className="border p-2">Work</th>
              <th className="border p-2">Late</th>
              <th className="border p-2">Early</th>
              <th className="border p-2">OT</th>
              <th className="border p-2">Status</th>
              <th className="border p-2">Remarks</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr><td colSpan="12" className="text-center p-4">Loading...</td></tr>
            ) : filteredData.length === 0 ? (
              <tr><td colSpan="12" className="text-center p-4">No data</td></tr>
            ) : filteredData.map((row) => {

              const rowColor = row.isAbsent
                ? "bg-red-50"
                : row.isLate
                  ? "bg-yellow-50"
                  : "";

              return (
                <tr key={row.empId} className={`${rowColor} hover:bg-blue-50`}>
                  <td className="border p-2 text-center">{row.empId}</td>
                  <td className="border p-2">{row.empName}</td>
                  <td className="border p-2 text-center">{row.attendanceDateDisplay}</td>
                  <td className="border p-2">{row.firstPunchDisplay || "-"}</td>
                  <td className="border p-2">{row.lastPunchDisplay || "-"}</td>
                  <td className="border p-2 text-center">{row.isAbsent ? "-" : row.totalPunches}</td>
                  <td className="border p-2 text-center">{row.isAbsent ? "-" : row.workingMinutes}</td>
                  <td className="border p-2 text-center">{row.isAbsent ? "-" : row.lateMinutes}</td>
                  <td className="border p-2 text-center">{row.isAbsent ? "-" : row.earlyLeaveMinutes}</td>
                  <td className="border p-2 text-center">{row.isAbsent ? "-" : row.overtimeMinutes}</td>
                  <td className="border p-2 text-center">
                    {row.isSuspense ? (
                      <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                        Suspense
                      </span>
                    ) : row.isAbsent ? (
                      <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs">
                        Absent
                      </span>
                    ) : row.isLate ? (
                      <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs">
                        Late
                      </span>
                    ) : row.isPresent ? (
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                        Present
                      </span>
                    ) : (
                      <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                        NA
                      </span>
                    )}
                  </td>
                  <td className="border p-2 text-xs">{row.remarks}</td>
                </tr>
              );
            })}
          </tbody>

        </table>
      </div>

      {/* ── MOBILE VIEW ── */}
      <div className="md:hidden space-y-3 mt-3">
        {loading ? (
          <div className="text-center p-4">Loading...</div>
        ) : filteredData.length === 0 ? (
          <div className="text-center text-gray-500">No data</div>
        ) : filteredData.map((row) => (
          <div key={row.empId} className="border rounded-xl p-3 shadow-sm">

            <div className="flex justify-between">
              <h3 className="font-semibold">{row.empName}</h3>
              {row.isAbsent ? "🔴" : row.isLate ? "🟡" : "🟢"}
            </div>

            <p className="text-xs text-gray-500">ID: {row.empId}</p>

            <div className="grid grid-cols-3 gap-2 text-xs mt-2 text-center">
              <div className="bg-gray-100 p-1 rounded">Punch<br />{row.isAbsent ? "-" : row.totalPunches}</div>
              <div className="bg-blue-100 p-1 rounded">Work<br />{row.isAbsent ? "-" : row.workingMinutes}</div>
              <div className="bg-yellow-100 p-1 rounded">Late<br />{row.isAbsent ? "-" : row.lateMinutes}</div>
            </div>

            <div className="text-xs mt-2 space-y-1">
              <p><b>Date:</b> {row.attendanceDateDisplay}</p>
              <p><b>First:</b> {row.firstPunchDisplay || "-"}</p>
              <p><b>Last:</b>  {row.lastPunchDisplay || "-"}</p>
              <p><b>Early Leave:</b> {row.isAbsent ? "-" : row.earlyLeaveMinutes}</p>
              <p><b>OT:</b> {row.isAbsent ? "-" : row.overtimeMinutes}</p>
              {row.remarks && <p><b>Remarks:</b> {row.remarks}</p>}
            </div>

          </div>
        ))}
      </div>

    </div>
  );
};

export default DailyAttendanceReport;